import React from "react";
import CustomerHealthSummary from "./CustomerHealthSummary";

export default function ClientRevenue({ dataFromParent, data }) {
  console.log("New_Client_Acquisition_6mon dataFromParent", dataFromParent);
  // Fallback to provided data if parent hasn't passed anything yet
  const dataset =
    dataFromParent && dataFromParent.length > 0
      ? dataFromParent[0]
      : {
          Current6MonthsNewClients: 0,
          Current6MonthsRevenue: 0,
          Previous6MonthsNewClients: 0,
          Previous6MonthsRevenue: 0,
        };

  const {
    Current6MonthsNewClients: curClients,
    Current6MonthsRevenue: curRev,
    Previous6MonthsNewClients: prevClients,
    Previous6MonthsRevenue: prevRev,
  } = dataset;

  // Calculate percentage growths
  const clientGrowth =
    prevClients > 0 ? ((curClients - prevClients) / prevClients) * 100 : 0;
  const revGrowth = prevRev > 0 ? ((curRev - prevRev) / prevRev) * 100 : 0;

  // Formatter utilities
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const formatNum = (val) => new Intl.NumberFormat().format(val);

  // Maximum width calculations for the comparative progress bars
  const maxClients = Math.max(curClients, prevClients);
  const maxRev = Math.max(curRev, prevRev);

  const clientCurPct = (curClients / maxClients) * 100;
  const clientPrevPct = (prevClients / maxClients) * 100;

  const revCurPct = (curRev / maxRev) * 100;
  const revPrevPct = (prevRev / maxRev) * 100;

  return (
    <div style={styles.container}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');
        .db-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .db-card:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02); }

        /* Desktop: KPI cards sit side-by-side (compact height), health summary to the right */
        .cr-grid {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
          gap: 12px;
          align-items: stretch;
        }
        .cr-cards {
          display: flex;
          flex-direction: row;
          gap: 10px;
        }
        .cr-cards .db-card {
          flex: 1;
          min-width: 0;
        }
        .cr-graph {
          height: 34px;
          margin-bottom: 12px;
        }
        .cr-cards .comparison-section {
          padding-top: 10px !important;
          gap: 6px !important;
        }

        /* Mobile: stack everything vertically */
        @media (max-width: 768px) {
          .cr-grid {
            grid-template-columns: 1fr;
          }
          .cr-cards {
            flex-direction: column;
          }
          .cr-graph {
            height: 50px;
            margin-bottom: 20px;
          }
          .cr-cards .comparison-section {
            padding-top: 16px !important;
            gap: 10px !important;
          }
        }
      `}</style>

      <div style={styles.header}>
        <p style={styles.subtitle}>6-Month comparative performance analysis</p>
      </div>

      <div className="cr-grid">
        <div className="cr-cards">
          {/* REVENUE KPI CARD */}
          <div className="db-card" style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardLabel}>6M REVENUE</span>
              <div style={{ ...styles.badge, ...styles.successBadge }}>
                +{revGrowth.toFixed(1)}%
              </div>
            </div>
            <div style={styles.mainValue}>{formatCurrency(curRev)}</div>

            {/* Legend Details */}
            <div style={styles.legendContainer}>
              <div style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: "#0D9488" }} />
                <span>Current Period</span>
              </div>
              <div style={styles.legendItem}>
                <span
                  style={{
                    ...styles.legendDot,
                    background: "#9CA3AF",
                    borderStyle: "dashed",
                    borderWeight: "1px",
                    backgroundColor: "transparent",
                  }}
                />
                <span style={{ color: "#6B7280" }}>Previous Period</span>
              </div>
            </div>

            {/* Overlaid Trend Graph Line */}
            <div className="cr-graph" style={{...styles.graphContainer, height: "auto", marginBottom: 0}}>
              <svg viewBox="0 0 300 50" style={styles.sparkline}>
                {/* Previous Period Baseline Line */}
                <path
                  d="M 10,42 L 75,41 L 150,43 L 225,40 L 290,38"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                  strokeLinecap="round"
                />

                {/* Current Period Growth Line */}
                <path
                  d="M 10,42 L 75,34 L 150,22 L 225,12 L 290,6"
                  fill="none"
                  stroke="#0D9488"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="290" cy="6" r="3.5" fill="#0D9488" />
              </svg>
            </div>

            <div className="comparison-section" style={styles.comparisonSection}>
              <div style={styles.barRow}>
                <span style={styles.barLabel}>Current 6M</span>
                <div style={styles.track}>
                  <div
                    style={{
                      ...styles.fill,
                      width: `${revCurPct}%`,
                      background: "#0D9488",
                    }}
                  />
                </div>
                <span style={styles.barValue}>{formatCurrency(curRev)}</span>
              </div>
              <div style={styles.barRow}>
                <span style={styles.barLabel}>Previous 6M</span>
                <div style={styles.track}>
                  <div
                    style={{
                      ...styles.fill,
                      width: `${revPrevPct}%`,
                      background: "#9CA3AF",
                    }}
                  />
                </div>
                <span style={styles.barValue}>{formatCurrency(prevRev)}</span>
              </div>
            </div>
          </div>

          {/* NEW CLIENTS KPI CARD */}
          <div className="db-card" style={styles.card}>
            <div style={styles.cardHeader}>
              <span style={styles.cardLabel}>6M NEW CLIENTS</span>
              <div style={{ ...styles.badge, ...styles.successBadge }}>
                +{clientGrowth.toFixed(1)}%
              </div>
            </div>
            <div style={styles.mainValue}>{formatNum(curClients)}</div>

            {/* Legend Details */}
            <div style={styles.legendContainer}>
              <div style={styles.legendItem}>
                <span style={{ ...styles.legendDot, background: "#3B82F6" }} />
                <span>Current Period</span>
              </div>
              <div style={styles.legendItem}>
                <span
                  style={{
                    ...styles.legendDot,
                    background: "#9CA3AF",
                    borderStyle: "dashed",
                    borderWeight: "1px",
                    backgroundColor: "transparent",
                  }}
                />
                <span style={{ color: "#6B7280" }}>Previous Period</span>
              </div>
            </div>

            {/* Overlaid Trend Graph Line */}
            <div className="cr-graph" style={{...styles.graphContainer, height: "auto", marginBottom: 0}}>
              <svg viewBox="0 0 300 50" style={styles.sparkline}>
                {/* Previous Period Baseline Line */}
                <path
                  d="M 10,42 L 75,40 L 150,41 L 225,39 L 290,38"
                  fill="none"
                  stroke="#9CA3AF"
                  strokeWidth="2"
                  strokeDasharray="4,4"
                  strokeLinecap="round"
                />

                {/* Current Period Growth Line */}
                <path
                  d="M 10,42 L 75,35 L 150,26 L 225,14 L 290,6"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <circle cx="290" cy="6" r="3.5" fill="#3B82F6" />
              </svg>
            </div>

            <div className="comparison-section" style={styles.comparisonSection}>
              <div style={styles.barRow}>
                <span style={styles.barLabel}>Current 6M</span>
                <div style={styles.track}>
                  <div
                    style={{
                      ...styles.fill,
                      width: `${clientCurPct}%`,
                      background: "#3B82F6",
                    }}
                  />
                </div>
                <span style={styles.barValue}>{formatNum(curClients)}</span>
              </div>
              <div style={styles.barRow}>
                <span style={styles.barLabel}>Previous 6M</span>
                <div style={styles.track}>
                  <div
                    style={{
                      ...styles.fill,
                      width: `${clientPrevPct}%`,
                      background: "#9CA3AF",
                    }}
                  />
                </div>
                <span style={styles.barValue}>{formatNum(prevClients)}</span>
              </div>
            </div>
          </div>
        </div>
        <div>
          <CustomerHealthSummary data={data} />
        </div>
      </div>
    </div>
  );
}

const styles = {
  header: {
    marginBottom: "4px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 4px 0",
    letterSpacing: "-0.02em",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6B7280",
    marginLeft: "10px",
  },
  card: {
    background: "#FFFFFF",
    border: "1px solid #E5E7EB",
    borderRadius: "14px",
    padding: "16px",
    boxSizing: "border-box",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },
  cardLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#6B7280",
    letterSpacing: "0.05em",
  },
  badge: {
    fontSize: "12px",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: "6px",
    fontFamily: "'JetBrains Mono', monospace",
  },
  successBadge: {
    background: "#ECFDF5",
    color: "#065F46",
  },
  mainValue: {
    fontSize: "28px",
    fontWeight: 700,
    color: "#111827",
    letterSpacing: "-0.02em",
    marginBottom: "8px",
  },
  legendContainer: {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
    fontSize: "11px",
    fontWeight: 500,
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    color: "#4B5563",
  },
  legendDot: {
    width: "12px",
    height: "3px",
    borderRadius: "1px",
    display: "inline-block",
  },
  graphContainer: {
    width: "100%",
    height: "50px",
    marginBottom: "20px",
  },
  sparkline: {
    width: "100%",
    height: "100%",
    overflow: "visible",
  },
  comparisonSection: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    borderTop: "1px solid #F3F4F6",
    paddingTop: "16px",
  },
  barRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    fontSize: "12px",
  },
  barLabel: {
    width: "80px",
    color: "#4B5563",
    fontWeight: 500,
  },
  track: {
    flex: 1,
    height: "6px",
    background: "#F3F4F6",
    borderRadius: "3px",
    overflow: "hidden",
  },
  fill: {
    height: "100%",
    borderRadius: "3px",
    transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  barValue: {
    width: "90px",
    textAlign: "right",
    fontFamily: "'JetBrains Mono', monospace",
    color: "#111827",
    fontWeight: 500,
  },
};